import { Inject, Injectable, DOCUMENT } from '@angular/core';

import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import {
  getCatExternalBaseUrl,
  getIsLearningStudioEnabled,
  getSdlc,
} from '@mhe/reader/core/state/configuration/configuration.selectors';
import { Env } from '@mhe/reader/types';
import {
  HardcodedInternalResolver,
  LocalStorageResolver,
  resolveFirstValidUrl,
  ScoutExternalResolver,
} from './cat-url-resolvers';

const ALLOWED_ENVIRONMENTS: Env[] = ['local', 'dev', 'qastg', 'qalv', 'prod'];
const NON_PROD_ENVIRONMENTS: Env[] = ['local', 'dev', 'qastg', 'qalv'];

/**
 * Service that injects a web component script into the HTML head.
 *
 * Call `initialize()` after LTI parameters are loaded (e.g. in AppComponent.ngOnInit)
 * so the store contains the final runtime config before the guards are evaluated.
 *
 * Only injects when:
 * 1. LTI `custom_learning_studio` is `true`
 * 2. Environment is local, dev, qastg, qalv, or prod (based on SDLC selector)
 * 3. URL resolution chain produces a valid http/https URL:
 *    - Non-prod: localStorage → hardcoded internal → Scout external
 *    - Prod: Scout external only
 */
@Injectable({
  providedIn: 'root',
})
export class CatWebcomponentLoaderService {
  constructor(
    @Inject(WINDOW) private readonly windowRef: Window,
    @Inject(DOCUMENT) private readonly documentRef: Document,
    private readonly store: Store,
  ) {}

  initialize(): void {
    const isLearningStudioEnabled = this.store.selectSignal(getIsLearningStudioEnabled)();
    if (!isLearningStudioEnabled) {
      console.log('[CatWebcomponentLoader] custom_learning_studio is not enabled, skipping script injection');
      return;
    }

    const sdlc = this.store.selectSignal(getSdlc)() as Env;
    if (!ALLOWED_ENVIRONMENTS.includes(sdlc)) {
      console.log('[CatWebcomponentLoader] Environment not allowed:', sdlc);
      return;
    }

    const scoutExternalBaseUrl = this.store.selectSignal(getCatExternalBaseUrl)();
    const isNonProd = NON_PROD_ENVIRONMENTS.includes(sdlc);

    const resolvers = isNonProd
      ? [
          new LocalStorageResolver(this.windowRef.localStorage),
          new HardcodedInternalResolver(sdlc),
          new ScoutExternalResolver(scoutExternalBaseUrl),
        ]
      : [new ScoutExternalResolver(scoutExternalBaseUrl)];

    const url = resolveFirstValidUrl(resolvers);

    if (!url) {
      console.warn('[CatWebcomponentLoader] No valid URL resolved for env:', sdlc);
      return;
    }

    this.injectScript(url, sdlc);
  }

  /**
   * Checks if a script with the given URL already exists in the document head.
   */
  private scriptExists(url: string): boolean {
    const scripts = this.documentRef.head.querySelectorAll('script');
    return Array.from(scripts).some(script => script.src === url);
  }

  /**
   * Injects a script tag into the document head with the provided URL.
   */
  private injectScript(url: string, env: string): void {
    if (this.scriptExists(url)) {
      console.log('[CatWebcomponentLoader] Script already exists in head, skipping:', url);
      return;
    }

    const scriptTag = this.documentRef.createElement('script');
    scriptTag.src = url;
    scriptTag.type = 'module';
    scriptTag.defer = true;
    scriptTag.dataset.env = env;
    scriptTag.onerror = (error) => {
      console.error('[CatWebcomponentLoader] Failed to load web component script:', url, error);
    };

    console.log('[CatWebcomponentLoader] Injecting web component script:', url, 'env:', env);
    this.documentRef.head.appendChild(scriptTag);
  }
}
